import { Button, Logo, Muted, color, space } from '@biopeak/ui';
import { useState } from 'react';
import { useAlertsRealtime, useUnacknowledgedCount } from './lib/alerts.ts';
import { useSession, type Doctor } from './lib/session.tsx';
import { NotAuthorized } from './screens/NotAuthorized.tsx';
import { AlertBadge, Notifications } from './screens/Notifications.tsx';
import { PatientProfile } from './screens/PatientProfile.tsx';
import { ResetPassword } from './screens/ResetPassword.tsx';
import { SignIn } from './screens/SignIn.tsx';
import { Triage } from './screens/Triage.tsx';

/**
 * Roteamento por ESTADO, mesmo princípio do app do paciente: o estado real
 * (existe sessão? existe médico vinculado?) decide a tela, não a URL.
 *
 * ÚNICA EXCEÇÃO: `/reset-password`. Esse caminho existe por causa do link
 * que o Supabase manda por e-mail — precisa ser um endereço fixo, então
 * precisa de rota de verdade. Também é a página que o app do PACIENTE usa
 * (ver `SignIn.tsx` de lá): os dois autenticam no mesmo projeto Supabase, e
 * o app nativo não tem rota web própria para hospedar isto.
 */
export function App() {
  const { loading, session, doctor, signOut } = useSession();

  if (window.location.pathname === '/reset-password') {
    return <ResetPassword />;
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', minHeight: '100vh', paddingTop: '120px' }}>
        <Muted>Carregando…</Muted>
      </div>
    );
  }

  if (!session) {
    return <SignIn />;
  }

  if (!doctor) {
    return <NotAuthorized />;
  }

  return <Shell doctor={doctor} signOut={signOut} />;
}

/**
 * O painel de quem já está autenticado.
 *
 * Componente separado porque os hooks de alerta consultam o banco: deixá-los no
 * `App` os faria rodar antes de existir sessão, e as regras dos hooks impedem
 * chamá-los depois dos returns antecipados. A fronteira aqui é exatamente a
 * fronteira da autenticação.
 */
function Shell({ doctor, signOut }: { doctor: Doctor; signOut: () => Promise<void> }) {
  // Mesma escolha do app do paciente: o estado decide a tela. Um paciente
  // aberto é um estado do painel, não uma rota — e como não há URL para
  // compartilhar, também não há link com id de paciente circulando por aí.
  const [openPatientId, setOpenPatientId] = useState<string | null>(null);
  const [showAlerts, setShowAlerts] = useState(false);

  useAlertsRealtime();
  const naoVistos = useUnacknowledgedCount();

  function abrirPaciente(id: string) {
    setShowAlerts(false);
    setOpenPatientId(id);
  }

  return (
    <div>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: `${space.sm} ${space.lg}`,
          background: color.white,
          borderBottom: `1px solid ${color.border}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: space.sm }}>
          <Logo variant="mark" size={22} />
          <Muted style={{ fontSize: '15px' }}>Dr(a). {doctor.full_name.split(' ')[0]}</Muted>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: space.sm }}>
          <AlertBadge
            count={naoVistos}
            onClick={() => {
              setOpenPatientId(null);
              setShowAlerts((v) => !v);
            }}
          />
          <Button variant="quiet" onClick={signOut}>
            Sair
          </Button>
        </div>
      </div>

      {showAlerts ? (
        <Notifications onOpenPatient={abrirPaciente} />
      ) : openPatientId ? (
        <PatientProfile patientId={openPatientId} onBack={() => setOpenPatientId(null)} />
      ) : (
        <Triage onOpenPatient={abrirPaciente} />
      )}
    </div>
  );
}
